import React from "react";
import { Box, Stack, Typography, Button, Chip } from "@mui/material";
import { Link as ScrollLink } from "react-scroll";
import { BsGithub, BsLinkedin, BsArrowRight } from "react-icons/bs";
import { HiOutlineMail } from "react-icons/hi";
import { colors, font, pagePx } from "../theme/tokens";

const stats = [
  { value: "10+", label: "Production SaaS shipped" },
  { value: "8", label: "Microservices architected" },
  { value: "2026", label: "Graduating Software Engineer" },
];

const stack = ["TypeScript", "NestJS", "FastAPI", "React", "Kafka", "Docker", "PostgreSQL"];

const Header = () => {
  return (
    <Box
      component="section"
      id="home"
      sx={{
        position: "relative",
        px: pagePx,
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 12 },
        overflow: "hidden",
      }}
    >
      {/* Aurora background */}
      <Box className="aurora" sx={{ width: 460, height: 460, background: "#4f46e5", top: -120, left: -80 }} />
      <Box className="aurora" sx={{ width: 380, height: 380, background: "#06b6d4", top: 40, right: -60, opacity: 0.35 }} />
      <Box
        className="dot-grid"
        sx={{ position: "absolute", inset: 0, opacity: 0.5, maskImage: "radial-gradient(ellipse at 50% 0%, black, transparent 75%)" }}
      />

      <Stack
        direction={{ xs: "column-reverse", md: "row" }}
        spacing={{ xs: 5, md: 6 }}
        sx={{ position: "relative", zIndex: 1, alignItems: "center", justifyContent: "space-between" }}
      >
        {/* Left: copy */}
        <Box sx={{ maxWidth: { md: "62%" } }}>
          <Chip
            label="Open to Software Engineer roles · Remote / Relocation"
            sx={{
              backgroundColor: "rgba(52, 211, 153, 0.12)",
              color: colors.green,
              border: "1px solid rgba(52, 211, 153, 0.3)",
              fontFamily: font.mono,
              fontSize: "12px",
              fontWeight: 500,
              mb: 3,
              "& .MuiChip-label": { px: 1.6 },
            }}
          />

          <Typography
            variant="h1"
            sx={{
              fontFamily: font.sans,
              fontWeight: 800,
              fontSize: { xs: "34px", sm: "44px", md: "54px" },
              lineHeight: 1.08,
              color: colors.ink,
              mb: 2.5,
            }}
          >
            Software Engineer building{" "}
            <Box component="span" className="grad-text">
              scalable, well-architected
            </Box>{" "}
            systems.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "16px", md: "18px" },
              color: colors.inkSoft,
              lineHeight: 1.75,
              maxWidth: 620,
              mb: 4,
            }}
          >
            I design and ship production software end-to-end — from{" "}
            <Box component="span" sx={{ color: colors.ink, fontWeight: 600 }}>
              distributed microservices
            </Box>{" "}
            and event-driven backends to polished product UIs. I care about clean
            architecture, resilience, and systems that hold up under real load.
          </Typography>

          {/* CTAs */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4 }}>
            <ScrollLink to="work" smooth offset={-90} duration={400}>
              <Button
                endIcon={<BsArrowRight />}
                sx={{
                  background: colors.gradPrimary,
                  color: "#0b1120",
                  fontFamily: font.sans,
                  fontWeight: 700,
                  fontSize: "15px",
                  textTransform: "none",
                  px: 3,
                  py: 1.3,
                  borderRadius: "12px",
                  boxShadow: "0 8px 30px rgba(99,102,241,0.35)",
                  "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 36px rgba(99,102,241,0.45)" },
                  transition: "all 0.2s ease",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                View my work
              </Button>
            </ScrollLink>
            <Button
              startIcon={<HiOutlineMail />}
              href="mailto:hamdaniibrahim.contact@gmail.com"
              sx={{
                color: colors.ink,
                fontFamily: font.sans,
                fontWeight: 600,
                fontSize: "15px",
                textTransform: "none",
                px: 3,
                py: 1.3,
                borderRadius: "12px",
                border: `1px solid ${colors.borderStrong}`,
                "&:hover": { borderColor: colors.accent, backgroundColor: "rgba(34,211,238,0.06)" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Get in touch
            </Button>
          </Stack>

          {/* Socials */}
          <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 4 }}>
            <a href="https://www.linkedin.com/in/ibrahimn9/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <BsLinkedin className="icon" />
            </a>
            <a href="https://github.com/ibrahimn9" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <BsGithub className="icon" />
            </a>
          </Stack>

          {/* Tech stack strip */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {stack.map((t) => (
              <Box
                key={t}
                sx={{
                  fontFamily: font.mono,
                  fontSize: "12.5px",
                  color: colors.inkSoft,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "8px",
                  px: 1.3,
                  py: 0.5,
                  backgroundColor: "rgba(255,255,255,0.02)",
                }}
              >
                {t}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right: portrait card */}
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Box
            sx={{
              position: "relative",
              width: { xs: 220, sm: 260, md: 300 },
              height: { xs: 220, sm: 260, md: 300 },
              borderRadius: "24px",
              p: "3px",
              background: colors.gradPrimary,
              boxShadow: "0 20px 60px rgba(79,70,229,0.35)",
            }}
          >
            <Box
              component="img"
              src="/assets/profile.jpg"
              alt="Ibrahim Hamdani"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "22px",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Stack>

      {/* Stats row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 2, sm: 4, md: 6 }}
        sx={{ position: "relative", zIndex: 1, mt: { xs: 6, md: 9 }, pt: 4, borderTop: `1px solid ${colors.border}` }}
      >
        {stats.map((s) => (
          <Box key={s.label} sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: font.sans, fontWeight: 800, fontSize: { xs: 28, md: 34 }, color: colors.ink }}>
              {s.value}
            </Typography>
            <Typography sx={{ fontSize: 14, color: colors.inkSoft }}>{s.label}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default Header;
